import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { Row, Col, Button } from 'reactstrap';
import moment from 'moment';
import Sidebar from '../../Sidebar';
import { useNavigate, useParams } from 'react-router';
import { useCookies } from 'react-cookie';
import { displayError } from '../../helpers';
import ErrorToast from '../../ErrorToast';
import FloatingControls from '../../components/FloatingControls';
import FormPageLayout from '../../components/FormPageLayout';
import LoadingOverlay from '../../components/LoadingOverlay';
import { getUserAllowedPlants } from '../../services/PlantServices';
import { getAllCustomerMain } from '../../services/CustomerMainServices';
import { getAllBank } from '../../services/BankService';
import {
    createReceiptVoucher,
    getInvoicesForReceiptVoucher,
    getReceiptVoucherDetails,
    getReceiptVoucherNumber,
    updateReceiptVoucher,
} from '../../services/ReceiptVoucherServices';
import { fetchAndShowReceiptVoucherPdf } from '../../utils/receiptVoucherPdf';
import {
    AccountingHintPanel,
    allocationRemaining,
    buildAutoAllocation,
    getBankCashOptions,
    maxLineAllocation,
    RECEIPT_AGAINST,
    RECEIPT_MODES,
    sortLinesOldestFirst,
    sumAllocation,
} from './VoucherUtils';
import { getCurrentDate } from '../../utils/DateUtils';

function displayDateToInput(value) {
    if (!value) {
        return '';
    }
    const parts = String(value).split('-');
    if (parts.length === 3 && parts[2].length === 4) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return value;
}

function ReceiptVoucher() {
    const { id: editId } = useParams();
    const isEditMode = Boolean(editId);
    const [cookies] = useCookies(['myToken']);
    const navigate = useNavigate();
    const [recordLoaded, setRecordLoaded] = useState(!isEditMode);
    const [inputs, setInputs] = useState({
        receipt_date: getCurrentDate(),
        bank_date: getCurrentDate(),
        receipt_mode: 'NEFT/RTGS',
        receipt_against: 'invoice',
        receipt_amount: '',
    });
    const [plants, setPlants] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [banks, setBanks] = useState([]);
    const [invoiceLines, setInvoiceLines] = useState([]);
    const [accountingHint, setAccountingHint] = useState(null);
    const [inProgress, setInProgress] = useState(false);
    const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
    const [selectedBankOption, setSelectedBankOption] = useState(null);

    const receiptAmount = parseFloat(inputs.receipt_amount) || 0;

    const allocatedTotal = useMemo(
        () => sumAllocation(invoiceLines, 'receipt_amt'),
        [invoiceLines],
    );

    const balanceToAllocate = useMemo(
        () => allocationRemaining(receiptAmount, allocatedTotal),
        [allocatedTotal, receiptAmount],
    );

    const loadInvoices = useCallback(() => {
        if (!inputs.plant_id || !inputs.customer_id || inputs.receipt_against !== 'invoice') {
            setInvoiceLines([]);
            return;
        }
        getInvoicesForReceiptVoucher(
            cookies,
            inputs.plant_id,
            inputs.customer_id,
            isEditMode ? editId : '',
        )
            .then((response) => {
                const lines = sortLinesOldestFirst(response.invoice_lines || []);
                const amount = parseFloat(inputs.receipt_amount) || 0;
                setInvoiceLines(
                    !isEditMode && amount > 0 && inputs.receipt_against === 'invoice'
                        ? buildAutoAllocation(lines, 'receipt_amt', amount)
                        : lines,
                );
                setAccountingHint(response.accounting_hint || null);
            })
            .catch(() => setInvoiceLines([]));
    }, [cookies, editId, inputs.plant_id, inputs.customer_id, inputs.receipt_against, inputs.receipt_amount, isEditMode]);

    useEffect(() => {
        getUserAllowedPlants(cookies).then((response) => {
            setPlants((response.plant_list || []).filter((p) => p.status).map((p) => ({
                value: p.id,
                label: p.plant_alias,
            })));
        });
        getAllCustomerMain(cookies).then((response) => {
            setCustomers((response.customer_list || []).filter((c) => c.status).map((c) => ({
                value: c.id,
                label: c.name,
            })));
        });
        getAllBank(cookies).then((response) => {
            setBanks(getBankCashOptions(response.bank_list || []));
        });
    }, [cookies]);

    useEffect(() => {
        if (!isEditMode || !editId) {
            return;
        }
        setInProgress(true);
        getReceiptVoucherDetails(cookies, editId)
            .then((record) => {
                if (!record.status) {
                    navigate(`/ReceiptVoucherEdit/${editId}`);
                    return;
                }
                setInputs({
                    plant_id: record.plant_id,
                    customer_id: record.customer_id,
                    bank_id: record.bank_id,
                    prefix: record.prefix,
                    sl_no: record.sl_no,
                    receipt_no: record.receipt_no,
                    receipt_date: displayDateToInput(record.receipt_date),
                    bank_date: displayDateToInput(record.bank_date),
                    receipt_mode: record.receipt_mode,
                    receipt_against: record.receipt_against,
                    receipt_amount: String(record.receipt_amount ?? ''),
                    instrument_no: record.instrument_no || '',
                    instrument_date: displayDateToInput(record.instrument_date),
                    bank_name: record.bank_name_text || '',
                    bank_branch: record.bank_branch || '',
                    deposited_branch: record.deposited_branch || '',
                    deposited_date: displayDateToInput(record.deposited_date),
                    remarks: record.remarks || '',
                });
                setSelectedCustomerOption({ value: record.customer_id, label: record.customer_name });
                setSelectedBankOption({ value: record.bank_id, label: record.bank_name });
                setAccountingHint(record.accounting_hint || null);
                const allocationMap = new Map(
                    (record.invoice_lines || []).map((line) => [line.invoice_id, line.receipt_amt]),
                );
                return getInvoicesForReceiptVoucher(cookies, record.plant_id, record.customer_id, editId)
                    .then((response) => {
                        const lines = sortLinesOldestFirst(response.invoice_lines || []);
                        setInvoiceLines(lines.map((line) => ({
                            ...line,
                            receipt_amt: allocationMap.has(line.invoice_id)
                                ? String(allocationMap.get(line.invoice_id))
                                : '',
                        })));
                    });
            })
            .catch((error) => displayError(error?.response?.data || error, 'Load Failed'))
            .finally(() => {
                setRecordLoaded(true);
                setInProgress(false);
            });
    }, [cookies, editId, isEditMode, navigate]);

    useEffect(() => {
        if (isEditMode || !inputs.plant_id || !inputs.receipt_date) {
            return;
        }
        getReceiptVoucherNumber(
            cookies,
            inputs.plant_id,
            moment(inputs.receipt_date).format('DD-MM-YYYY'),
        ).then((response) => {
            setInputs((values) => ({
                ...values,
                prefix: response.prefix,
                sl_no: response.receipt_no,
                receipt_no: `${response.prefix}/${response.receipt_no}`,
            }));
        }).catch((error) => {
            displayError(error?.response?.data || error, 'Receipt voucher number');
        });
    }, [cookies, inputs.plant_id, inputs.receipt_date, isEditMode]);

    useEffect(() => {
        if (!recordLoaded || isEditMode) {
            return;
        }
        loadInvoices();
    }, [isEditMode, loadInvoices, recordLoaded]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setInputs((values) => ({ ...values, [name]: value }));
    };

    const handleReceiptAmountChange = (event) => {
        setInputs((values) => ({ ...values, receipt_amount: event.target.value }));
    };

    const handleReceiptAmountBlur = () => {
        autoAllocate();
    };

    const handleSelectChange = (name, option) => {
        if (name === 'customer_id') {
            setSelectedCustomerOption(option);
        }
        if (name === 'bank_id') {
            setSelectedBankOption(option);
        }
        setInputs((values) => ({ ...values, [name]: option ? option.value : '' }));
    };

    const handleInvoiceAmtChange = (index, value) => {
        setInvoiceLines((lines) => lines.map((line, i) => {
            if (i !== index) {
                return line;
            }
            if (value === '' || value === null) {
                return { ...line, receipt_amt: '' };
            }
            const maxFill = maxLineAllocation(line, lines, 'receipt_amt', receiptAmount, index);
            let amount = parseFloat(value);
            if (Number.isNaN(amount)) {
                return { ...line, receipt_amt: value };
            }
            if (amount < 0) {
                amount = 0;
            }
            if (amount > maxFill) {
                amount = maxFill;
            }
            return { ...line, receipt_amt: amount > 0 ? amount.toFixed(2) : '' };
        }));
    };

    const fillInvoiceLine = (index) => {
        setInvoiceLines((lines) => lines.map((line, i) => {
            if (i !== index) {
                return line;
            }
            const maxFill = maxLineAllocation(line, lines, 'receipt_amt', receiptAmount, index);
            return { ...line, receipt_amt: maxFill > 0 ? maxFill.toFixed(2) : '' };
        }));
    };

    const autoAllocate = () => {
        if (receiptAmount <= 0) {
            return;
        }
        setInvoiceLines((lines) => buildAutoAllocation(lines, 'receipt_amt', receiptAmount));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const receiptTotal = parseFloat(inputs.receipt_amount) || 0;
        if (!inputs.plant_id || !inputs.customer_id || !inputs.bank_id) {
            Swal.fire('Validation', 'Plant, Customer and Bank are required.', 'warning');
            return;
        }
        if (receiptTotal <= 0) {
            Swal.fire('Validation', 'Receipt amount must be greater than zero.', 'warning');
            return;
        }
        if (inputs.receipt_against === 'invoice') {
            if (Math.abs(allocatedTotal - receiptTotal) > 0.05) {
                Swal.fire('Validation', 'Invoice allocation must match receipt amount.', 'warning');
                return;
            }
        }
        setInProgress(true);
        const payload = {
            plant_id: inputs.plant_id,
            customer_id: inputs.customer_id,
            bank_id: inputs.bank_id,
            prefix: inputs.prefix,
            sl_no: inputs.sl_no,
            receipt_date: moment(inputs.receipt_date).format('DD-MM-YYYY'),
            bank_date: moment(inputs.bank_date).format('DD-MM-YYYY'),
            receipt_mode: inputs.receipt_mode,
            receipt_against: inputs.receipt_against,
            receipt_amount: receiptTotal,
            instrument_no: inputs.instrument_no || '',
            instrument_date: inputs.instrument_date
                ? moment(inputs.instrument_date).format('DD-MM-YYYY') : '',
            bank_name: inputs.bank_name || '',
            bank_branch: inputs.bank_branch || '',
            deposited_branch: inputs.deposited_branch || '',
            deposited_date: inputs.deposited_date
                ? moment(inputs.deposited_date).format('DD-MM-YYYY') : '',
            remarks: inputs.remarks || '',
            invoice_lines: inputs.receipt_against === 'invoice'
                ? invoiceLines
                    .filter((line) => parseFloat(line.receipt_amt) > 0)
                    .map((line) => ({
                        invoice_id: line.invoice_id,
                        receipt_amt: parseFloat(line.receipt_amt),
                    }))
                : [],
        };
        const savePromise = isEditMode
            ? updateReceiptVoucher(cookies, editId, payload)
            : createReceiptVoucher(cookies, payload);

        savePromise
            .then((response) => {
                Swal.fire('Saved', response.message || (isEditMode ? 'Receipt voucher updated.' : 'Receipt voucher saved.'), 'success');
                navigate(`/ReceiptVoucherEdit/${response.id || editId}`);
            })
            .catch((error) => displayError(error?.response?.data || error, 'Save Failed'))
            .finally(() => setInProgress(false));
    };

    const handlePrint = () => {
        if (!editId) {
            return;
        }
        setInProgress(true);
        fetchAndShowReceiptVoucherPdf(cookies, editId, { title: 'Receipt Voucher' })
            .finally(() => setInProgress(false));
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <FormPageLayout
                    moduleLabel="ACCOUNTS"
                    title="Receipt Voucher"
                    subtitle={isEditMode ? (inputs.receipt_no || 'Edit receipt voucher') : 'Record customer receipt against bank/cash — Dr Bank/Cash, Cr Sundry Debtors.'}
                    modeLabel={isEditMode ? 'EDIT MODE' : 'ADD MODE'}
                >
                    <form onSubmit={handleSubmit}>
                        <AccountingHintPanel hint={accountingHint} />
                        <Row className="form-row table-bordered shadow p-2 my-2 border-secondary form-control-panel">
                            <Col sm={6}>
                                <label>Plant *</label>
                                <Select
                                    options={plants}
                                    value={plants.find((p) => p.value === inputs.plant_id) || null}
                                    onChange={(opt) => handleSelectChange('plant_id', opt)}
                                    placeholder="Select plant"
                                />
                            </Col>
                            <Col sm={3}>
                                <label>Receipt No</label>
                                <input className="form-control" name="receipt_no" value={inputs.receipt_no || ''} readOnly />
                            </Col>
                            <Col sm={3}>
                                <label>Receipt Date *</label>
                                <input type="date" className="form-control" name="receipt_date" value={inputs.receipt_date} onChange={handleChange} />
                            </Col>
                        </Row>
                        <Row className="form-row table-bordered shadow p-2 my-2 border-secondary form-control-panel">
                            <Col sm={6}>
                                <label>Customer *</label>
                                <Select
                                    options={customers}
                                    value={selectedCustomerOption}
                                    onChange={(opt) => handleSelectChange('customer_id', opt)}
                                    placeholder="Select customer"
                                />
                            </Col>
                            <Col sm={6}>
                                <label>Deposit Bank / Cash *</label>
                                <Select
                                    options={banks}
                                    value={selectedBankOption}
                                    onChange={(opt) => handleSelectChange('bank_id', opt)}
                                    placeholder="Select bank or cash account"
                                />
                            </Col>
                        </Row>
                        <Row className="form-row table-bordered shadow p-2 my-2 border-secondary form-control-panel">
                            <Col sm={3}>
                                <label>Receipt Mode *</label>
                                <select className="form-control" name="receipt_mode" value={inputs.receipt_mode} onChange={handleChange}>
                                    {RECEIPT_MODES.map((mode) => (
                                        <option key={mode} value={mode}>{mode}</option>
                                    ))}
                                </select>
                            </Col>
                            <Col sm={3}>
                                <label>Receipt Against *</label>
                                <select className="form-control" name="receipt_against" value={inputs.receipt_against} onChange={handleChange}>
                                    {RECEIPT_AGAINST.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </Col>
                            <Col sm={3}>
                                <label>Bank Date *</label>
                                <input type="date" className="form-control" name="bank_date" value={inputs.bank_date} onChange={handleChange} />
                            </Col>
                            <Col sm={3}>
                                <label>Receipt Amount *</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    className="form-control"
                                    name="receipt_amount"
                                    value={inputs.receipt_amount}
                                    onChange={handleReceiptAmountChange}
                                    onBlur={handleReceiptAmountBlur}
                                />
                            </Col>
                        </Row>
                        <Row className="form-row table-bordered shadow p-2 my-2 border-secondary form-control-panel">
                            <Col sm={3}>
                                <label>Instrument No</label>
                                <input className="form-control" name="instrument_no" maxLength={6} value={inputs.instrument_no || ''} onChange={handleChange} />
                            </Col>
                            <Col sm={3}>
                                <label>Instrument Date</label>
                                <input type="date" className="form-control" name="instrument_date" value={inputs.instrument_date || ''} onChange={handleChange} />
                            </Col>
                            <Col sm={3}>
                                <label>Deposited Branch</label>
                                <input className="form-control" name="deposited_branch" value={inputs.deposited_branch || ''} onChange={handleChange} />
                            </Col>
                            <Col sm={3}>
                                <label>Deposited Date</label>
                                <input type="date" className="form-control" name="deposited_date" value={inputs.deposited_date || ''} onChange={handleChange} />
                            </Col>
                        </Row>
                        <Row className="form-row table-bordered shadow p-2 my-2 border-secondary form-control-panel">
                            <Col sm={12}>
                                <label>Remarks</label>
                                <textarea className="form-control" name="remarks" rows={2} value={inputs.remarks || ''} onChange={handleChange} />
                            </Col>
                        </Row>

                        {inputs.receipt_against === 'invoice' && (
                            <div className="form-row table-bordered shadow p-2 my-2 border-secondary form-control-panel">
                                <div className="d-flex justify-content-between align-items-center mb-2 px-2 flex-wrap">
                                    <strong>Invoice Allocation</strong>
                                    <div className="d-flex align-items-center flex-wrap gap-2">
                                        <span className={`small mr-2 ${balanceToAllocate > 0.05 ? 'text-warning font-weight-bold' : balanceToAllocate < -0.05 ? 'text-danger font-weight-bold' : 'text-success'}`}>
                                            Balance to allocate: {balanceToAllocate.toFixed(2)}
                                        </span>
                                        <Button type="button" color="secondary" size="sm" onClick={autoAllocate}>
                                            Auto Allocate
                                        </Button>
                                        <Button type="button" color="light" size="sm" onClick={() => setInvoiceLines((lines) => lines.map((line) => ({ ...line, receipt_amt: '' })))}>
                                            Clear
                                        </Button>
                                    </div>
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-sm table-bordered mb-0">
                                        <thead>
                                            <tr>
                                                <th>Invoice No</th>
                                                <th>Date</th>
                                                <th>Site</th>
                                                <th>Invoice Amt</th>
                                                <th>Received</th>
                                                <th>Balance</th>
                                                <th>Can Fill</th>
                                                <th>Receipt Amt</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {invoiceLines.length === 0 ? (
                                                <tr><td colSpan={8} className="text-center text-muted">No outstanding invoices</td></tr>
                                            ) : invoiceLines.map((line, index) => {
                                                const canFill = maxLineAllocation(
                                                    line,
                                                    invoiceLines,
                                                    'receipt_amt',
                                                    receiptAmount,
                                                    index,
                                                );
                                                return (
                                                <tr key={line.invoice_id}>
                                                    <td>{line.invoice_no}</td>
                                                    <td>{line.invoice_date}</td>
                                                    <td>{line.site_name}</td>
                                                    <td>{line.invoice_amt}</td>
                                                    <td>{line.received_amt}</td>
                                                    <td>{line.balance_amt}</td>
                                                    <td>
                                                        <button
                                                            type="button"
                                                            className="btn btn-link btn-sm p-0"
                                                            disabled={canFill <= 0 || receiptAmount <= 0}
                                                            onClick={() => fillInvoiceLine(index)}
                                                            title="Fill up to remaining receipt balance"
                                                        >
                                                            {canFill > 0 ? canFill.toFixed(2) : '—'}
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            className="form-control form-control-sm"
                                                            value={line.receipt_amt}
                                                            max={canFill}
                                                            placeholder={canFill > 0 ? canFill.toFixed(2) : ''}
                                                            onChange={(e) => handleInvoiceAmtChange(index, e.target.value)}
                                                        />
                                                    </td>
                                                </tr>
                                                );
                                            })}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan={6} className="text-right"><strong>Receipt Amount</strong></td>
                                                <td colSpan={2}><strong>{receiptAmount.toFixed(2)}</strong></td>
                                            </tr>
                                            <tr>
                                                <td colSpan={6} className="text-right"><strong>Allocated Total</strong></td>
                                                <td colSpan={2}><strong>{allocatedTotal.toFixed(2)}</strong></td>
                                            </tr>
                                            <tr className={balanceToAllocate > 0.05 ? 'table-warning' : balanceToAllocate < -0.05 ? 'table-danger' : 'table-success'}>
                                                <td colSpan={6} className="text-right"><strong>Balance to Allocate</strong></td>
                                                <td colSpan={2}><strong>{balanceToAllocate.toFixed(2)}</strong></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        )}

                        <Row className="form-row table-bordered shadow p-2 my-2 border-secondary form-control-panel">
                            <Col className="d-flex justify-content-end">
                                {isEditMode && (
                                    <Button color="success" type="button" className="mr-2" onClick={handlePrint} disabled={!recordLoaded}>
                                        Print
                                    </Button>
                                )}
                                <Button color="primary" type="submit" className="mr-2" disabled={inProgress || !recordLoaded}>
                                    {isEditMode ? 'Update' : 'Save'}
                                </Button>
                                <Button color="secondary" type="button" onClick={() => navigate('/ReceiptVoucherTable')}>
                                    Cancel
                                </Button>
                            </Col>
                        </Row>
                    </form>
                </FormPageLayout>
                <LoadingOverlay active={inProgress} />
                <ErrorToast />
                <FloatingControls
                    tableLink="/ReceiptVoucherTable"
                    onCancel={() => navigate('/ReceiptVoucherTable')}
                    enableCancel
                />
            </div>
        </div>
    );
}

export default ReceiptVoucher;
